import { useState } from 'react';
import { Menu, X, ChevronDown, ArrowRight, Phone, Mail, MapPin, Factory, Shield, Users, Zap } from 'lucide-react';
import MJLogo from '../components/MJLogo';

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState('');

  const products = [
    { name: 'Silica Sand Grade A', desc: 'High purity for precision casting', span: 'md:col-span-2 md:row-span-2' },
    { name: 'Resin Coated Sand', desc: 'Superior strength for shell molding', span: '' },
    { name: 'Foundry Sand', desc: 'Thermal stability for metal casting', span: '' },
    { name: 'Brown Silica Sand', desc: 'Quality brown silica for foundries', span: 'md:col-span-2' },
    { name: 'White Silica Sand', desc: 'Pure white for glass manufacturing', span: '' },
    { name: 'Custom Blends', desc: 'Tailored to your specifications', span: '' },
  ];

  const faqs = [
    { q: 'What is the purity level of your Silica Sand?', a: 'Our Grade A Silica Sand maintains a purity of 99.5% SiO2, ensuring minimal impurities for high-end industrial applications.' },
    { q: 'Do you offer bulk transport logistics?', a: 'Yes, we manage end-to-end logistics for bulk orders across India, ensuring timely delivery to your facility.' },
    { q: 'Can I get a custom grain size distribution?', a: 'Absolutely. We process sand to specific AFS grain fineness numbers based on your foundry or construction requirements.' },
    { q: 'What is your production capacity?', a: 'We have a production capacity of 1800 tons/month for wash silica sand and 1200 tons/month for resin coated sand.' },
  ];

  const stats = [
    { value: '2019', label: 'ESTABLISHED', icon: Factory },
    { value: '100%', label: 'QUALITY', icon: Shield },
    { value: '50+', label: 'CLIENTS', icon: Users },
    { value: '1800T', label: 'MONTHLY', icon: Zap },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('Thank you for your inquiry. Our team will contact you within 24 hours.');
    setFormData({ name: '', company: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@500;700&family=Manrope:wght@400;500;600;700&display=swap" rel="stylesheet" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <MJLogo size={48} />
              <div>
                <p className="text-white font-bold text-lg sm:text-xl" style={{ fontFamily: "'Oswald', sans-serif" }}>M&J Enterprises</p>
                <p className="text-[#FFB703] text-xs sm:text-sm font-medium italic">We do it anyway!</p>
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {['Home', 'About', 'Products', 'FAQ', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-[#A3A3A3] hover:text-white text-sm font-medium tracking-wide transition-colors"
                  data-testid={`nav-${item.toLowerCase()}`}
                >
                  {item.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0A0A] border-t border-white/5 py-6 px-6">
            {['Home', 'About', 'Products', 'FAQ', 'Contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="block w-full text-left py-3 text-[#A3A3A3] hover:text-white text-sm font-medium tracking-wide"
              >
                {item.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20 pb-32 sm:pb-20">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url('https://images.unsplash.com/photo-1684067241390-32e697757ae7?auto=compress&cs=tinysrgb&w=1920')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-12 sm:py-20">
          <div className="max-w-3xl">
            <p className="text-[#FFB703] text-xs sm:text-sm font-bold tracking-[0.3em] mb-4 sm:mb-6">SINCE 2019 • ANDHRA PRADESH</p>
            <h1 
              className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-6 sm:mb-8"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              M&J<br />
              <span className="text-[#FFB703]">Enterprises</span>
            </h1>
            <p className="text-base sm:text-xl text-[#A3A3A3] leading-relaxed mb-8 sm:mb-10 max-w-xl">
              Premium Industrial Silica Sand & Resin Coated Sand. Powering foundries and construction across India.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => scrollToSection('products')}
                className="group px-6 sm:px-8 py-3 sm:py-4 bg-[#FFB703] text-black font-bold text-sm tracking-wide hover:bg-[#FB8500] transition-all flex items-center justify-center gap-2"
                data-testid="hero-cta-products"
              >
                EXPLORE PRODUCTS
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white/20 text-white font-bold text-sm tracking-wide hover:border-white/40 transition-all"
                data-testid="hero-cta-contact"
              >
                CONTACT US
              </button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#121212] border-t border-white/5 hidden sm:block">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {stats.map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={i} className={`py-8 px-4 text-center ${i > 0 ? 'border-l border-white/5' : ''}`}>
                    <Icon className="w-5 h-5 text-[#FFB703] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Oswald', sans-serif" }}>{stat.value}</p>
                    <p className="text-xs text-[#A3A3A3] tracking-wider">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#FFB703] text-sm font-bold tracking-[0.3em] mb-4">ABOUT US</p>
              <h2 
                className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                FORGED IN<br />ANDHRA PRADESH
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-6">
                MJ Enterprises isn't just a supplier; we are the backbone of industrial strength. 
                From precision casting to heavy construction, our silica sand is the foundation of modern infrastructure.
              </p>
              <p className="text-[#A3A3A3] leading-relaxed mb-8">
                With state-of-the-art processing facilities and rigorous quality control, 
                we deliver consistent, high-grade materials that meet the most demanding specifications.
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Production Capacity', value: '1800 T/M' },
                  { label: 'Quality Assurance', value: '99.5%' },
                  { label: 'Pan-India Delivery', value: '24/7' },
                ].map((item, i) => (
                  <div key={i} className="bg-[#121212] p-4 border border-white/5">
                    <p className="text-[#FFB703] text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{item.value}</p>
                    <p className="text-xs text-[#A3A3A3] mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1238864/pexels-photo-1238864.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Industrial operations"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-48 h-48 border-2 border-[#FFB703] -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 md:py-32 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#FFB703] text-sm font-bold tracking-[0.3em] mb-4">OUR PRODUCTS</p>
            <h2 
              className="text-4xl md:text-5xl font-bold text-white"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              INDUSTRIAL GRADE MATERIALS
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {products.map((product, i) => (
              <div
                key={i}
                className={`group bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#FFB703]/50 transition-all duration-300 cursor-pointer ${product.span}`}
                onClick={() => scrollToSection('contact')}
              >
                <div className="w-10 h-10 bg-[#262626] flex items-center justify-center mb-4 group-hover:bg-[#FFB703] transition-colors">
                  <div className="w-3 h-3 bg-[#FFB703] group-hover:bg-black transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {product.name.toUpperCase()}
                </h3>
                <p className="text-sm text-[#A3A3A3]">{product.desc}</p>
                <div className="mt-4 flex items-center gap-2 text-[#FFB703] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Get Quote <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#FFB703] text-sm font-bold tracking-[0.3em] mb-4">FAQ</p>
            <h2 
              className="text-4xl md:text-5xl font-bold text-white"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              TECHNICAL QUERIES
            </h2>
          </div>

          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/5 bg-[#121212]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#1A1A1A] transition-colors"
                  data-testid={`faq-${i}`}
                >
                  <span className="font-semibold text-white pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FFB703] flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-[#A3A3A3] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <p className="text-[#FFB703] text-sm font-bold tracking-[0.3em] mb-4">CONTACT</p>
              <h2 
                className="text-4xl md:text-5xl font-bold text-white mb-8"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                START A PROJECT
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Your Name *"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full h-14 px-4 bg-[#1A1A1A] border border-white/10 text-white placeholder-[#666] focus:border-[#FFB703] outline-none transition-colors"
                    data-testid="contact-name"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full h-14 px-4 bg-[#1A1A1A] border border-white/10 text-white placeholder-[#666] focus:border-[#FFB703] outline-none transition-colors"
                    data-testid="contact-company"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Email *"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full h-14 px-4 bg-[#1A1A1A] border border-white/10 text-white placeholder-[#666] focus:border-[#FFB703] outline-none transition-colors"
                    data-testid="contact-email"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full h-14 px-4 bg-[#1A1A1A] border border-white/10 text-white placeholder-[#666] focus:border-[#FFB703] outline-none transition-colors"
                    data-testid="contact-phone"
                  />
                </div>
                <textarea
                  placeholder="Your Message *"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-4 bg-[#1A1A1A] border border-white/10 text-white placeholder-[#666] focus:border-[#FFB703] outline-none transition-colors resize-none"
                  data-testid="contact-message"
                />
                <button
                  type="submit"
                  className="w-full sm:w-auto px-10 py-4 bg-[#FFB703] text-black font-bold text-sm tracking-wide hover:bg-[#FB8500] transition-colors"
                  data-testid="contact-submit"
                >
                  SEND INQUIRY
                </button>
                {formStatus && (
                  <p className="text-[#FFB703] font-medium mt-4">{formStatus}</p>
                )}
              </form>
            </div>

            {/* Info */}
            <div className="lg:pl-12">
              <div className="bg-[#1A1A1A] border border-white/5 p-8 md:p-10 h-full">
                <h3 className="text-xl font-bold text-white mb-8" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  GET IN TOUCH
                </h3>
                
                <div className="space-y-8">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#262626] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#FFB703]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1">Address</p>
                      <p className="text-[#A3A3A3] text-sm leading-relaxed" data-testid="contact-address">
                        1-B, Rajupalem,<br />
                        Ozili Mandalam,<br />
                        Tirupathi DIST. AP-524402,<br />
                        India
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#262626] flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#FFB703]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1">Phone</p>
                      <div className="space-y-1">
                        <p className="text-[#A3A3A3] text-sm">
                          <span className="text-white/80">Mujahid Shaik</span>{' '}
                          <a href="tel:+919032377039" className="text-[#A3A3A3] hover:text-[#FFB703] transition-colors" data-testid="contact-phone-mujahid">
                            +91 9032377039
                          </a>
                        </p>
                        <p className="text-[#A3A3A3] text-sm">
                          <span className="text-white/80">Junaid Mohammed</span>{' '}
                          <a href="tel:+918142429865" className="text-[#A3A3A3] hover:text-[#FFB703] transition-colors" data-testid="contact-phone-junaid">
                            +91 8142429865
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#262626] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#FFB703]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1">Email</p>
                      <div className="space-y-1">
                        <a href="mailto:info@mjenterprises.net" className="block text-[#A3A3A3] text-sm hover:text-[#FFB703] transition-colors" data-testid="contact-email-info">
                          info@mjenterprises.net
                        </a>
                        <a href="mailto:MDJunaid@mjenterprises.net" className="block text-[#A3A3A3] text-sm hover:text-[#FFB703] transition-colors" data-testid="contact-email-junaid">
                          MDJunaid@mjenterprises.net
                        </a>
                        <a href="mailto:SKMujahid@mjenterprises.net" className="block text-[#A3A3A3] text-sm hover:text-[#FFB703] transition-colors" data-testid="contact-email-mujahid">
                          SKMujahid@mjenterprises.net
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-white/5">
                  <p className="text-sm text-[#666] mb-2">Business Hours</p>
                  <p className="text-white">Monday - Saturday: 9:00 AM - 6:00 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div>
                <p className="text-white font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>M&J Enterprises</p>
                <p className="text-[#FFB703] text-xs italic">We do it anyway!</p>
              </div>
            </div>
            <p className="text-[#666] text-sm">
              © {new Date().getFullYear()} M&J Enterprises. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
